import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { HubWriteError, hubService } from '../services/hub.service';
import {
  blockScoreLabel,
  botCountLabel,
  changesEmbedder,
  collectionsSummary,
  createBody,
  deleteBlockedReason,
  emptyForm,
  formFromRow,
  needsModelConfirm,
  probeMessage,
  saveError,
  updateBody,
  validateForm,
  type CollectionStatus,
  type EmbeddingProfileRow,
  type FormErrors,
  type ProbeResult,
  type ProfileForm,
} from '../lib/embeddingProfilesView';

type Editing = { mode: 'create' } | { mode: 'edit'; row: EmbeddingProfileRow } | null;

const input = 'w-full rounded border border-gray-300 px-2 py-1.5 text-sm disabled:bg-gray-100 disabled:text-gray-500';
const btn = 'rounded border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50';
const primary = 'rounded bg-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50';
const TONE = { green: 'text-green-700', amber: 'text-amber-700', red: 'text-red-700' } as const;

/**
 * Platform › Embedding profiles (global admins): an embedder and the Qdrant collections built with it. Every bot on
 * a profile uses its embedder, its search thresholds and its linked-block thresholds; bots pick a profile in their
 * AI config. id, vector size and collection suffix are fixed once created (the server refuses changes too).
 */
export function EmbeddingProfiles() {
  const [rows, setRows] = useState<EmbeddingProfileRow[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [editing, setEditing] = useState<Editing>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [collections, setCollections] = useState<{ id: string; list: CollectionStatus[] } | null>(null);
  const [busyRow, setBusyRow] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setRows(await hubService.listEmbeddingProfilesAdmin());
      setLoadError(null);
    } catch (e) {
      setLoadError(e instanceof HubWriteError ? saveError(e.status, e.body) : String(e));
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const checkCollections = async (id: string) => {
    setBusyRow(id);
    const r = await hubService
      .writeEmbeddingProfile<CollectionStatus[]>('POST', `/${encodeURIComponent(id)}/collections`)
      .finally(() => setBusyRow(null));
    if (r.ok) setCollections({ id, list: r.data });
    else setNotice(saveError(r.status, r.message));
  };

  const remove = async (row: EmbeddingProfileRow) => {
    if (!window.confirm(`Delete profile "${row.label}" (${row.id})? Its Qdrant collections are kept.`)) return;
    setBusyRow(row.id);
    const r = await hubService.writeEmbeddingProfile('DELETE', `/${encodeURIComponent(row.id)}`).finally(() => setBusyRow(null));
    setNotice(r.ok ? `Deleted ${row.id}.` : saveError(r.status, r.message));
    if (r.ok) load();
  };

  const saved = (msg: string, list?: { id: string; list: CollectionStatus[] }) => {
    setEditing(null);
    setNotice(msg);
    if (list) setCollections(list);
    load();
  };

  return (
    <div className="space-y-4 p-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold">Embedding profiles</h1>
          <p className="mt-1 max-w-3xl text-sm text-gray-600">
            An embedder and the Qdrant collections built with it. Every bot on a profile uses its search and
            linked-block thresholds. Bots choose a profile in their AI config; moving a bot needs its knowledge
            re-embedded first.
          </p>
        </div>
        {!editing && (
          <button type="button" className={primary} onClick={() => { setNotice(null); setEditing({ mode: 'create' }); }}>
            New profile
          </button>
        )}
      </div>

      {notice && (
        <div role="status" className="flex items-start justify-between rounded border border-blue-200 bg-blue-50 px-3 py-2 text-sm text-blue-900">
          <span>{notice}</span>
          <button type="button" className="ml-4 text-blue-700 hover:underline" onClick={() => setNotice(null)}>Dismiss</button>
        </div>
      )}

      {editing && (
        <ProfileEditor
          key={editing.mode === 'edit' ? editing.row.id : 'new'}
          editing={editing}
          onCancel={() => setEditing(null)}
          onSaved={saved}
        />
      )}

      {collections && (
        <section className="rounded border border-gray-200 bg-white p-4">
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-semibold">Collections for {collections.id}</h2>
            <button type="button" className="text-sm text-gray-500 hover:underline" onClick={() => setCollections(null)}>Close</button>
          </div>
          <CollectionList list={collections.list} />
          <button type="button" className={`${btn} mt-3`} disabled={busyRow === collections.id} onClick={() => checkCollections(collections.id)}>
            {busyRow === collections.id ? 'Checking…' : 'Check again'}
          </button>
        </section>
      )}

      {loadError && <p className="rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">{loadError}</p>}
      {!rows && !loadError && <p className="text-sm text-gray-500">Loading…</p>}

      {rows && (
        <div className="overflow-x-auto rounded border border-gray-200 bg-white">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-left text-gray-600">
              <tr>
                <th className="px-4 py-2 font-semibold">Profile</th>
                <th className="px-4 py-2 font-semibold">Embedder</th>
                <th className="px-4 py-2 font-semibold">Collections</th>
                <th className="px-4 py-2 font-semibold">Search</th>
                <th className="px-4 py-2 font-semibold">Linked blocks</th>
                <th className="px-4 py-2 font-semibold">Used by</th>
                <th className="px-4 py-2" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {rows.map((r) => {
                const blocked = deleteBlockedReason(r);
                return (
                  <tr key={r.id} className="align-top">
                    <td className="px-4 py-3">
                      <div className="font-medium">
                        {r.label}
                        {r.isDefault && <span className="ml-2 rounded bg-blue-100 px-1.5 py-0.5 text-xs font-medium text-blue-800">Default</span>}
                      </div>
                      <div className="font-mono text-xs text-gray-500">{r.id}</div>
                      {r.updatedBy && <div className="mt-1 text-xs text-gray-400">Changed by {r.updatedBy}</div>}
                    </td>
                    <td className="px-4 py-3">
                      <div>{r.modelName}</div>
                      <div className="break-all text-xs text-gray-500">{r.embedderUrl}</div>
                    </td>
                    <td className="px-4 py-3">
                      <div>{r.dim}-dim</div>
                      <div className="font-mono text-xs text-gray-500">{r.collectionSuffix ? `*${r.collectionSuffix}` : 'no suffix'}</div>
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-700">
                      <div>Initial ≥ {r.initThreshold}, final ≥ {r.finalThreshold}</div>
                      <div>Top {r.maxResults}, multi-vector +{r.multiVectorBoost}</div>
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-700">
                      <div>Block only ≥ {blockScoreLabel(r.blockOnlyScore, 'blockOnlyScore')}</div>
                      <div>With the answer ≥ {blockScoreLabel(r.blockAttachScore, 'blockAttachScore')}</div>
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-700">{botCountLabel(r)}</td>
                    <td className="whitespace-nowrap px-4 py-3 text-right">
                      <div className="flex justify-end gap-2">
                        <button type="button" className={btn} onClick={() => { setNotice(null); setEditing({ mode: 'edit', row: r }); }}>
                          Edit
                        </button>
                        <button type="button" className={btn} disabled={busyRow === r.id} onClick={() => checkCollections(r.id)}>
                          Collections
                        </button>
                        <span title={blocked ?? undefined}>
                          <button type="button" className={`${btn} text-red-700`} disabled={!!blocked || busyRow === r.id} onClick={() => remove(r)}>
                            Delete
                          </button>
                        </span>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function CollectionList({ list }: { list: CollectionStatus[] }) {
  const sum = collectionsSummary(list);
  return (
    <div className="mt-2 space-y-2">
      <p className={`text-sm font-medium ${TONE[sum.tone]}`}>{sum.text}</p>
      <ul className="grid gap-1 text-xs sm:grid-cols-2">
        {list.map((c, i) => (
          <li key={`${c.owner}-${c.name}-${i}`} className="flex items-center gap-2">
            <span className={c.error || c.exists === false ? 'text-red-600' : c.exists ? 'text-green-600' : 'text-amber-600'}>
              {c.error || c.exists === false ? '✕' : c.exists ? '✓' : '?'}
            </span>
            <span className="font-mono">{c.name || c.owner}</span>
            {c.error && <span className="text-red-700">{c.error}</span>}
          </li>
        ))}
      </ul>
    </div>
  );
}

function Field({ label, hint, error, children }: { label: string; hint?: string; error?: string; children: ReactNode }) {
  return (
    <label className="block text-sm">
      <span className="font-medium text-gray-700">{label}</span>
      <div className="mt-1">{children}</div>
      {error ? <span className="mt-0.5 block text-xs text-red-700">{error}</span> : hint && <span className="mt-0.5 block text-xs text-gray-500">{hint}</span>}
    </label>
  );
}

function ProfileEditor({
  editing,
  onCancel,
  onSaved,
}: {
  editing: Exclude<Editing, null>;
  onCancel: () => void;
  onSaved: (msg: string, collections?: { id: string; list: CollectionStatus[] }) => void;
}) {
  const isEdit = editing.mode === 'edit';
  const before = isEdit ? editing.row : null;
  const [form, setForm] = useState<ProfileForm>(before ? formFromRow(before) : emptyForm());
  const [errors, setErrors] = useState<FormErrors>({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [probe, setProbe] = useState<ProbeResult | null>(null);
  const [probing, setProbing] = useState(false);

  const set = <K extends keyof ProfileForm>(k: K, v: ProfileForm[K]) => {
    setForm((f) => ({ ...f, [k]: v }));
    if (k === 'embedderUrl' || k === 'modelName' || k === 'dim') setProbe(null);
  };

  const runProbe = async () => {
    const e = validateForm(form, isEdit ? 'edit' : 'create');
    const relevant = { embedderUrl: e.embedderUrl, modelName: e.modelName, dim: e.dim };
    if (relevant.embedderUrl || relevant.modelName || relevant.dim) {
      setErrors((x) => ({ ...x, ...relevant }));
      return;
    }
    setProbing(true);
    const r = await hubService
      .writeEmbeddingProfile<ProbeResult>('POST', '/probe', {
        embedderUrl: form.embedderUrl.trim(), modelName: form.modelName.trim(), dim: Number(form.dim),
      })
      .finally(() => setProbing(false));
    if (r.ok) setProbe(r.data);
    else setError(saveError(r.status, r.message));
  };

  const save = async () => {
    const e = validateForm(form, isEdit ? 'edit' : 'create');
    setErrors(e);
    setError(null);
    if (Object.keys(e).length) return;
    if (before) {
      const body = updateBody(before, form);
      if (!Object.keys(body).length) return onCancel();
      if (body.isDefault && !window.confirm(
        `Make "${form.label}" the default? Bots with no AI config yet, and every new bot, use the default and read ` +
        'its collections — only do this once their knowledge is embedded there.',
      )) return;
      const put = (b: Record<string, unknown>) =>
        hubService.writeEmbeddingProfile('PUT', `/${encodeURIComponent(before.id)}`, b);
      setBusy(true);
      try {
        let r = await put(body);
        const ask = r.ok ? null : needsModelConfirm(r.status, r.message);
        if (ask) {
          if (!window.confirm(`${ask}\n\nChange the model anyway?`)) return;
          r = await put({ ...body, confirmModelChange: true });
        }
        if (!r.ok) return setError(saveError(r.status, r.message));
        return onSaved(`Saved ${before.id}. Bots pick the change up within a minute.`);
      } finally {
        setBusy(false);
      }
    }
    if (form.isDefault && !window.confirm(
      `Make "${form.label}" the default? Bots with no AI config yet, and every new bot, will use it — and its collections start empty.`,
    )) return;
    setBusy(true);
    const r = await hubService
      .writeEmbeddingProfile<{ profile: EmbeddingProfileRow; collections: CollectionStatus[] }>('POST', '', createBody(form))
      .finally(() => setBusy(false));
    if (!r.ok) return setError(saveError(r.status, r.message));
    onSaved(`Created ${r.data.profile.id}. Its collections are empty until bots are re-embedded into them.`, {
      id: r.data.profile.id,
      list: r.data.collections,
    });
  };

  const locked = isEdit ? 'Fixed after create — bots and collections depend on it' : undefined;
  const probeText = probe ? probeMessage(probe, Number(form.dim)) : null;
  const embedderChanged = before ? changesEmbedder(before, form) : false;

  return (
    <section className="space-y-5 rounded border border-gray-200 bg-white p-5">
      <h2 className="text-base font-semibold">{before ? `Edit ${before.label}` : 'New embedding profile'}</h2>

      <fieldset className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <legend className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">Identity</legend>
        <Field label="Id" hint={locked ?? 'Bots point at it, e.g. bge_m3'} error={errors.id}>
          <input className={`${input} font-mono`} value={form.id} disabled={isEdit} onChange={(e) => set('id', e.target.value)} />
        </Field>
        <Field label="Name" error={errors.label}>
          <input className={input} value={form.label} onChange={(e) => set('label', e.target.value)} />
        </Field>
        <Field label="Vector size (dim)" hint={locked ?? 'What the embedder returns, e.g. 1024'} error={errors.dim}>
          <input className={input} inputMode="numeric" value={form.dim} disabled={isEdit} onChange={(e) => set('dim', e.target.value)} />
        </Field>
        <Field label="Collection suffix" hint={locked ?? 'Added to every collection name, e.g. _bge_m3'} error={errors.collectionSuffix}>
          <input className={`${input} font-mono`} value={form.collectionSuffix} disabled={isEdit} onChange={(e) => set('collectionSuffix', e.target.value)} />
        </Field>
      </fieldset>

      <fieldset className="grid gap-4 sm:grid-cols-2">
        <legend className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">Embedder</legend>
        <Field label="Base URL" hint="No path — the hub calls /v1/embeddings on it" error={errors.embedderUrl}>
          <input className={input} value={form.embedderUrl} placeholder="https://pool-bge.chatbot.mn" onChange={(e) => set('embedderUrl', e.target.value)} />
        </Field>
        <Field label="Model" error={errors.modelName}>
          <input className={input} value={form.modelName} placeholder="bge-m3" onChange={(e) => set('modelName', e.target.value)} />
        </Field>
        <div className="flex flex-wrap items-center gap-3 sm:col-span-2">
          <button type="button" className={btn} disabled={probing} onClick={runProbe}>
            {probing ? 'Testing…' : 'Test embedder'}
          </button>
          {probeText && <span className={`text-sm ${TONE[probeText.tone]}`}>{probeText.text}</span>}
          {embedderChanged && !probe && (
            <span className="text-xs text-gray-500">Saving tests the new embedder first and refuses another vector size.</span>
          )}
        </div>
      </fieldset>

      <fieldset className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <legend className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">Search</legend>
        <Field label="Initial threshold" hint="Similarity a hit needs before reranking" error={errors.initThreshold}>
          <input className={input} inputMode="decimal" value={form.initThreshold} onChange={(e) => set('initThreshold', e.target.value)} />
        </Field>
        <Field label="Final threshold" hint="Score a hit needs to be used" error={errors.finalThreshold}>
          <input className={input} inputMode="decimal" value={form.finalThreshold} onChange={(e) => set('finalThreshold', e.target.value)} />
        </Field>
        <Field label="Max results" error={errors.maxResults}>
          <input className={input} inputMode="numeric" value={form.maxResults} onChange={(e) => set('maxResults', e.target.value)} />
        </Field>
        <Field label="Multi-vector boost" hint="Added for collections with named vectors" error={errors.multiVectorBoost}>
          <input className={input} inputMode="decimal" value={form.multiVectorBoost} onChange={(e) => set('multiVectorBoost', e.target.value)} />
        </Field>
      </fieldset>

      <fieldset className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <legend className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">Linked blocks</legend>
        <Field label="Block only from" hint="Empty = default (0.80, or env BLOCK_ONLY_SCORE)" error={errors.blockOnlyScore}>
          <input className={input} inputMode="decimal" placeholder="0.80" value={form.blockOnlyScore} onChange={(e) => set('blockOnlyScore', e.target.value)} />
        </Field>
        <Field label="With the answer from" hint="Empty = default (0.60, or env BLOCK_ATTACH_SCORE)" error={errors.blockAttachScore}>
          <input className={input} inputMode="decimal" placeholder="0.60" value={form.blockAttachScore} onChange={(e) => set('blockAttachScore', e.target.value)} />
        </Field>
        <p className="text-xs text-gray-500 sm:col-span-2 lg:self-center">
          Read the shadow decisions before changing these. The Knowledge › Blocks test box and the bots use the same values.
        </p>
      </fieldset>

      <label className="flex items-start gap-2 text-sm">
        <input
          type="checkbox"
          className="mt-0.5"
          checked={form.isDefault}
          disabled={before?.isDefault}
          onChange={(e) => set('isDefault', e.target.checked)}
        />
        <span>
          <span className="font-medium">Default profile</span>
          <span className="block text-xs text-gray-500">
            {before?.isDefault
              ? 'This is the default. To change it, make another profile the default.'
              : 'Bots with no AI config yet, and every new bot, use the default. Only one profile can be the default.'}
          </span>
        </span>
      </label>

      {error && <p className="rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">{error}</p>}

      <div className="flex gap-2">
        <button type="button" className={primary} disabled={busy} onClick={save}>
          {busy ? (isEdit ? 'Saving…' : 'Creating…') : isEdit ? 'Save' : 'Create profile and collections'}
        </button>
        <button type="button" className={btn} disabled={busy} onClick={onCancel}>Cancel</button>
      </div>
    </section>
  );
}
